import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, X, BedDouble, Bath, Maximize } from "lucide-react";
import { api, fmtPrice, fmtBaths } from "../lib/api";
import { useFavorites } from "../hooks/useFavorites";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";

const ROWS = [
  ["Price", (p) => fmtPrice(p.price, p.currency)],
  ["Location", (p) => p.location],
  ["Type", (p) => p.property_type],
  ["Status", (p) => p.status],
  ["Bedrooms", (p) => p.beds],
  ["Bathrooms", (p) => fmtBaths(p.baths)],
  ["Area (sqft)", (p) => p.area?.toLocaleString()],
  ["Features", (p) => (p.features || []).join(", ")],
];

export default function Compare() {
  const { ids, toggle } = useFavorites();
  const [all, setAll] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/properties").then((r) => setAll(r.data));
    api.get("/profile").then((r) => setProfile(r.data));
  }, []);

  const saved = all.filter((p) => ids.includes(p.id)).slice(0, 3);

  return (
    <div className="bg-[#FAF9F6] min-h-screen">
      <Nav name={profile?.name} logo={profile?.logo} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-32 pb-24">
        <Link to="/" data-testid="compare-back" className="inline-flex items-center gap-2 text-sm text-[var(--slate)] hover:text-[#0A1128] transition-colors mb-8">
          <ArrowLeft size={16} /> Back to Collection
        </Link>
        <span className="text-xs uppercase tracking-[0.24em] text-[var(--gold)]">Side by Side</span>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight text-[#0A1128] mt-4 mb-12">Compare Homes</h1>

        {saved.length === 0 ? (
          <div data-testid="compare-empty" className="bg-white border border-dashed border-[#0A1128]/15 p-20 text-center text-[var(--slate)]">
            You haven&apos;t saved any homes yet. Tap the heart on a listing, then compare up to three here.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="compare-table">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr>
                  <th className="w-40" />
                  {saved.map((p) => (
                    <th key={p.id} data-testid={`compare-col-${p.id}`} className="p-4 align-top text-left border-b border-[#0A1128]/10">
                      <div className="relative hover-img h-40 mb-4">
                        <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover" />
                        <button data-testid={`compare-remove-${p.id}`} onClick={() => toggle(p.id)} className="absolute top-2 right-2 bg-[#0A1128] text-white p-1.5"><X size={13} /></button>
                      </div>
                      <Link to={`/property/${p.id}`} className="font-serif text-xl text-[#0A1128] link-underline">{p.title}</Link>
                      <div className="flex gap-4 mt-3 text-xs text-[var(--slate)]">
                        <span className="flex items-center gap-1"><BedDouble size={13} /> {p.beds}</span>
                        <span className="flex items-center gap-1"><Bath size={13} /> {fmtBaths(p.baths)}</span>
                        <span className="flex items-center gap-1"><Maximize size={13} /> {p.area?.toLocaleString()}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, fn], i) => (
                  <tr key={label} className={i % 2 ? "bg-[#E8E4D9]/25" : ""}>
                    <td className="p-4 text-xs uppercase tracking-[0.14em] text-[var(--slate)] align-top">{label}</td>
                    {saved.map((p) => (
                      <td key={p.id} className="p-4 text-[#0A1128] text-sm align-top">{fn(p)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer profile={profile} />
    </div>
  );
}
